import {Button} from 'platform/components';
import {HStack, Show, VStack} from 'platform/foundation';

import {mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {DmsDataGrid, type DmsDataGridProps, refreshDmsDatagrid} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {USER_ABSENCE_GRID_CODE, USER_ABSENCE_OVERVIEW_GRID_CODE} from '../constants/constants';
import {useUserAbsenceActions} from '../hooks/useUserAbsenceActions';
import {useUserAbsencePermissions} from '../hooks/useUserAbsencePermissions';
import {openCreateUserAbsenceDialog} from '../utils/openUserAbsenceDialog';

interface UserAbsenceGridProps extends RequiredTestIdProps, Pick<DmsDataGridProps, 'maximization'> {
  userId: string;
  isReadOnly?: boolean;
  autoHeight?: boolean;
}

export function UserAbsenceGrid(props: UserAbsenceGridProps) {
  const permissions = useUserAbsencePermissions(props.userId);
  const isReadOnly = props.isReadOnly || !permissions.canRead;
  const canCreate = !props.isReadOnly && permissions.canCreate;
  const canUpdate = !props.isReadOnly && permissions.canUpdate;
  const canDelete = !props.isReadOnly && permissions.canDelete;
  const handleAction = useUserAbsenceActions({'data-testid': props['data-testid']});

  const queryModifier = (filter: QueryFilterObject) => mergeAll([filter, {userId: props.userId}]);

  const refreshGrids = () => {
    refreshDmsDatagrid(USER_ABSENCE_GRID_CODE);
    refreshDmsDatagrid(USER_ABSENCE_OVERVIEW_GRID_CODE);
  };

  const handleCreate = () =>
    openCreateUserAbsenceDialog({
      userId: props.userId,
      onSuccess: refreshGrids,
      'data-testid': props['data-testid'],
    });

  const actionCallback: ActionCallback = ({actionKey, rowId}) =>
    handleAction({
      actionKey,
      userId: props.userId,
      userAbsenceId: isArray(rowId) ? rowId[0] : rowId,
      canUpdate,
      canDelete,
    });

  return (
    <VStack spacing={4} align="stretch">
      <Show when={canCreate}>
        <HStack justify="flex-end">
          <Button
            title={i18n.t('general.actions.add')}
            onClick={handleCreate}
            data-testid={suffixTestId('add-button', props)}
          />
        </HStack>
      </Show>

      <DmsDataGrid
        maximization={props.maximization}
        gridCode={USER_ABSENCE_GRID_CODE}
        queryModifier={queryModifier}
        actionCallback={actionCallback}
        disableAllActions={isReadOnly || (!canUpdate && !canDelete)}
        autoHeight={props.autoHeight === false ? undefined : true}
        emptyState={{
          headline: i18n.t('general.labels.noData'),
        }}
        data-testid={props['data-testid']}
      />
    </VStack>
  );
}
